import React, { useMemo } from "react";
import { Container, Graphics } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import {
  usePrimaryUIToolbarDimensionsV2,
  TOOLBAR_FADE_WIDTH,
} from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import CompanyLogo from "../CompanyLogo/CompanyLogo";

// Shared toolbar background styling
const TOOLBAR_BACKGROUND_COLOR = 0x090f41;
const TOOLBAR_BACKGROUND_OPACITY = 0.6;

// Top toolbar height multipliers
const TOOLBAR_TOP_HEIGHT_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 1.0,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 1.0,
  },
  [PixiSettings.DEVICE.DESKTOP]: 1.0,
};

/**
 * Draws a toolbar background rectangle with faded edges
 */
const drawToolbarBackground = (g, width, height, opacity) => {
  g.clear();
  g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity);
  g.drawRect(0, 0, width, height);
  g.endFill();

  const fadeWidth = TOOLBAR_FADE_WIDTH;
  // Left fade — sqrt easing (stays opaque longer, fades very gradually)
  for (let i = 0; i < fadeWidth; i++) {
    const t = (fadeWidth - i) / fadeWidth; // 1 → 0
    const eased = t * t; // quadratic: smooth fade that reaches true zero
    g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity * eased);
    g.drawRect(-i - 1, 0, 1, height);
    g.endFill();
  }
  // Right fade — sqrt easing
  for (let i = 0; i < fadeWidth; i++) {
    const t = (fadeWidth - i) / fadeWidth;
    const eased = t * t;
    g.beginFill(TOOLBAR_BACKGROUND_COLOR, opacity * eased);
    g.drawRect(width + i, 0, 1, height);
    g.endFill();
  }
};

/**
 * ToolbarBackgrounds — renders ONLY the colored background rectangles
 * for all toolbars. Rendered behind GameContainer (low zIndex) so game
 * effects can visually bleed over the toolbar backgrounds.
 */
const ToolbarBackgrounds = () => {
  const toolbar = usePrimaryUIToolbarDimensionsV2();
  const { dimensions, device, orientation, getResponsiveConfig } =
    useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Top toolbar dimensions
  const topToolbar = useMemo(() => {
    const heightMultiplier =
      device === PixiSettings.DEVICE.DESKTOP
        ? TOOLBAR_TOP_HEIGHT_CONFIG[device]
        : (TOOLBAR_TOP_HEIGHT_CONFIG[device]?.[orientation] ?? 0.9);
    return {
      width: toolbar.width,
      height: buttonConfig.height * heightMultiplier,
    };
  }, [toolbar.width, device, orientation, buttonConfig.height]);

  const toolbarX = (dimensions.width - toolbar.width) / 2;
  const topToolbarX = (dimensions.width - topToolbar.width) / 2;

  const bottomY = isMobilePortrait
    ? dimensions.height - toolbar.height * 2 - 8
    : dimensions.height - toolbar.height;

  const bottomOpacity = isMobilePortrait ? 0.35 : TOOLBAR_BACKGROUND_OPACITY;

  return (
    <Container name="toolbar_backgrounds" zIndex={1}>
      {/* Top toolbar background */}
      <Graphics
        name="toolbar_top_bg"
        x={topToolbarX}
        y={0}
        draw={(g) =>
          drawToolbarBackground(
            g,
            topToolbar.width,
            topToolbar.height,
            TOOLBAR_BACKGROUND_OPACITY,
          )
        }
      />

      {/* Company logo — in background layer so game effects render on top */}
      <Container x={topToolbarX} y={0}>
        <CompanyLogo toolbar={topToolbar} />
      </Container>

      {/* Bottom toolbar background */}
      <Graphics
        name="toolbar_bottom_bg"
        x={toolbarX}
        y={bottomY}
        draw={(g) =>
          drawToolbarBackground(g, toolbar.width, toolbar.height, bottomOpacity)
        }
      />

      {/* Bottom second toolbar background (mobile portrait only) */}
      {isMobilePortrait && (
        <Graphics
          name="toolbar_bottom_second_bg"
          x={toolbarX}
          y={dimensions.height - toolbar.height}
          draw={(g) =>
            drawToolbarBackground(
              g,
              toolbar.width,
              toolbar.height,
              TOOLBAR_BACKGROUND_OPACITY,
            )
          }
        />
      )}
    </Container>
  );
};

export default ToolbarBackgrounds;
